import React, { useEffect, useState } from "react";
import {
  useRouteState,
  SearchInput,
  SearchFiltersContainer,
  FilterValue, // eslint-disable-line no-unused-vars
  FilterTypes,
  PageHeading,
  AdvancedFilterTypes,
  SystemFeatures,
  ConfirmationModal,
} from "isuppli-react-components";
import { useDispatch } from "react-redux";
// eslint-disable-next-line no-unused-vars
import { AdvancedFilterInfo } from "isuppli-react-components/dist/Components/Search/Interfaces/AdvancedFilterInfo";
import { useLocation } from "react-router";
import Page from "../../Containers/Page/Page";
import ExportType from "../../util/Enumerators/ExportType";

import {
  AuditEntriesMessage, // eslint-disable-line no-unused-vars
  getAuditEntries,
  getUsers,
  getBusinessUnits,
  AuditEntrySearchMessage, // eslint-disable-line no-unused-vars
} from "../../http/audit/audit";
import { showError } from "../../store/actions";

import AuditTable from "./AuditTable/AuditTable";
import SubSystem, { subSystemToString } from "../../util/Enumerators/SubSystem";

export interface UserDetails {
  value: number;
  display: string;
}

const Audit = () => {
  const [routeSearchTerm, setRouteSearchTerm] = useRouteState("q", "");
  const [searchTerm, setSearchTerm] = useState("");
  const [isValidSearch, setIsValidSearch] = useState(false);

  const [limit, setLimit] = useRouteState("l", 20);
  const [offset, setOffset] = useRouteState("o", 0);

  const [routeFilterValues, setRouteFilterValues] = useRouteState<FilterValue[]>("f", []);
  const [filterValues, setFilterValues] = useState<FilterValue[]>(routeFilterValues);
  const [requestedUserFilterOptions, setRequestedUserFilterOptions] = useState([
    {
      value: 0,
      display: "",
    },
  ]);
  const [businessUnits, setBusinessUnits] = useState([
    {
      value: 0,
      display: "",
    },
  ]);

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<AuditEntriesMessage>();
  const dispatch = useDispatch();
  const location = useLocation();

  const baseUrl = "/api/v1/auditEntries/export";
  const [exportUrl, setExportUrl] = useState(baseUrl);

  const [showMinSearchError, setShowMinSearchError] = useState(false);

  const updateExportUrl = (
    searchCriteria: AuditEntrySearchMessage
  ) => {
    let newExportUrl = `${baseUrl}?limit=${searchCriteria.limit}&offset=${searchCriteria.offset}`;
    if (searchCriteria.startDate) newExportUrl = `${newExportUrl}&startDate=${searchCriteria.startDate}`;
    if (searchCriteria.endDate) newExportUrl = `${newExportUrl}&endDate=${searchCriteria.endDate}`;
    if (searchCriteria.searchTerm) newExportUrl = `${newExportUrl}&searchTerm=${searchCriteria.searchTerm}`;
    if (searchCriteria.requestedUserIdList) newExportUrl = `${newExportUrl}&requestedUserId=${searchCriteria.requestedUserIdList}`;
    if (searchCriteria.businessUnitIdList) newExportUrl = `${newExportUrl}&businessUnitId=${searchCriteria.businessUnitIdList}`;
    if (searchCriteria.subSystems) {
      for (let i = 0; i < searchCriteria.subSystems.length; i += 1) {
        newExportUrl += `&subSystems=${searchCriteria.subSystems[i]}`;
      }
    }
    setExportUrl(newExportUrl);
  };

  // Runs only the first render
  useEffect(() => {
    setSearchTerm(routeSearchTerm);
    setRouteFilterValues(routeFilterValues);
  }, []);

  useEffect(() => {
    const loader = async () => {
      setLoading(true);
      try {
        const dateFilter: any = routeFilterValues[1];
        const requestedUserFilterValues: any = routeFilterValues[2];
        const requestedBusinessUnitFilterValues: any = routeFilterValues[3];
        const advancedRegNameSearchTermValue: any = routeFilterValues[4];
        const advancedRegNumberSearchTermValue: any = routeFilterValues[5];
        let startDate = null;
        let endDate = null;
        if (dateFilter != null) {
          [startDate, endDate] = dateFilter;
        }
        const searchCriteria: AuditEntrySearchMessage = {
          limit,
          offset,
          subSystems:
            routeFilterValues[0] == null
              ? []
              : (routeFilterValues[0] as SubSystem[]),
          startDate: startDate ?? undefined,
          endDate: endDate ?? undefined,
          searchTerm: routeSearchTerm,
          requestedUserIdList:
            requestedUserFilterValues !== undefined
              ? requestedUserFilterValues
              : [],
          businessUnitIdList: requestedBusinessUnitFilterValues != null
            ? requestedBusinessUnitFilterValues
            : [],
          advancedRegNameSearchTerm: advancedRegNameSearchTermValue ?? "",
          advancedRegNumberSearchTerm: advancedRegNumberSearchTermValue ?? "",
        };

        // api call
        if (isValidSearch) {
          const result = await getAuditEntries(searchCriteria);
          setData(result);
          // update export url
          updateExportUrl(searchCriteria);
        }
      } catch (error) {
        dispatch(showError());
      }

      setLoading(false);
    };
    loader();
  }, [
    location,
    dispatch,
    limit,
    offset,
    routeSearchTerm,
    routeFilterValues,
    isValidSearch,
  ]);

  useEffect(() => {
    const loader = async () => {
      try {
        const businessUnitFilters: { value: number; display: string }[] = [];
        const staticbusinessUnitData: any = await getBusinessUnits();

        staticbusinessUnitData.forEach((businessUnitData: any) => {
          businessUnitFilters.push({
            value: businessUnitData.id,
            display: businessUnitData.shortName,
          });
        });

        setBusinessUnits(businessUnitFilters);
      } catch (error) {
        dispatch(showError());
      }
    };
    loader();
  }, [dispatch, setBusinessUnits]);

  useEffect(() => {
    const loader = async () => {
      try {
        const result: UserDetails[] = await getUsers();
        setRequestedUserFilterOptions(result);
      } catch (error) {
        dispatch(showError());
      }
    };
    loader();
  }, [dispatch, setRequestedUserFilterOptions]);

  const onPerformSearch = async () => {
    if (searchTerm == null || searchTerm.length < 3) {
      setShowMinSearchError(true);
      setIsValidSearch(false);
      return;
    }

    setIsValidSearch(true);
    setOffset(0);
    setRouteSearchTerm(searchTerm);
    setRouteFilterValues(filterValues ?? []);
  };

  const clearSearch = () => {
    setRouteSearchTerm("");
    setSearchTerm("");
    setRouteFilterValues([]);
    setFilterValues([]);
  };

  const filters = [
    {
      filterType: FilterTypes.MultiSelectDropDown,
      filterName: "System Area",
      options: Object.values(SubSystem)
        .filter((value) => typeof value === "number")
        .map((value) => value as SubSystem)
        .map((value) => ({
          display: subSystemToString(value),
          value,
        }))
        .sort((a, b) => (a.display > b.display ? 1 : -1)),
    },
    {
      filterName: "Date Range",
      options: [],
      filterType: FilterTypes.DateRangeInput,
    },
    {
      filterName: "Requested Name",
      options: requestedUserFilterOptions,
      filterType: FilterTypes.MultiSelectDropDown,
      canSearch: true,
      disabled: requestedUserFilterOptions.length < 1,
    },
    {
      filterName: "Business Units",
      propName: "businessUnits",
      options: businessUnits,
      filterType: FilterTypes.MultiSelectDropDown,
      disabled: businessUnits.length <= 1,
    },
  ];

  const advancedFiltersPropConfig = [
    {
      filterName: "Supplier Registered Name",
      placeholder: "Enter registered name...",
      filterType: AdvancedFilterTypes.Text,
    },
    {
      filterName: "Supplier Registration No. ",
      placeholder: "Enter registration number...",
      filterType: AdvancedFilterTypes.Text,
    }] as AdvancedFilterInfo[];

  return (
    <Page>
      <PageHeading heading="Audit Trail" menuItems={[]} />
      {showMinSearchError && (
        <ConfirmationModal
          heading="Error"
          hasCancel={false}
          description="Three (3) or more characters required to search"
          proceedButtonText="Ok"
          onProceedClick={() => setShowMinSearchError(false)}
          onToggleModal={() => { }}
        />
      )}
      <SearchInput
        searchTerm={searchTerm}
        onSearch={async () => {
          if (searchTerm == null || searchTerm.length < 3) {
            setShowMinSearchError(true);
            return;
          }
          setOffset(0);
          setRouteSearchTerm(searchTerm);
        }}
        onChange={(value: string) => {
          setSearchTerm(value);
        }}
        searchPlaceholder="Search for an audit entry"
        className="mb-4"
      />

      <SearchFiltersContainer
        filters={filters}
        buttons={[
          {
            key: "clear",
            label: "Clear",
            onClick: clearSearch,
            color: "secondary",
          },
          {
            key: "search",
            label: "Search",
            onClick: onPerformSearch,
            color: "primary",
          },
        ]}
        className="mb-4"
        filterValues={filterValues ?? []}
        advancedFilters={advancedFiltersPropConfig}
        advancedFiltersSystemFeature={SystemFeatures.None}
        onChange={setFilterValues}
      />
      {isValidSearch
      && (
        <>
          <div className="d-flex flex-row-reverse mt-1">
            <div>
              <a
                href={`/api/v1/token/getfile?url=${encodeURIComponent(`${exportUrl}&exportType=${ExportType.PDF}`)}`}
                download="audits.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className={`btn btn-link w-100 text-right px-0 text-capitalize ${loading ? "disabled" : ""}`}
              >
                Download PDF
              </a>
              <a
                href={`/api/v1/token/getfile?url=${encodeURIComponent(`${exportUrl}&exportType=${ExportType.Excel}`)}`}
                download="audits.xlsx"
                target="_blank"
                rel="noopener noreferrer"
                className={`btn btn-link w-100 text-right px-0 mx-0 text-capitalize ${loading ? "disabled" : ""}`}
              >
                Download Excel
              </a>
            </div>
          </div>
          <AuditTable
            loading={loading}
            onPageChange={(l, o) => {
              setLimit(l);
              setOffset(o);
            }}
            data={{
              items: data?.items ?? [],
              limit,
              offset,
              totalItems: data?.totalItems ?? 0,
            }}
          />
        </>
      )}
    </Page>
  );
};

export default Audit;
